import React from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Videocam, CallEnd } from '@mui/icons-material';
import { useCallingContext } from './CallingContext';
import { CallState } from '../../utils/webrtc-config';

interface VideoCallButtonProps {
  conversationId: string;
  agentId: string;
  customerInfo: {
    customer_id: string;
    name: string;
    email?: string;
  };
}

/**
 * Video call initiation button — styled to match the MuiIconButton size="small"
 * pattern used throughout the conversation header toolbar.
 */
export const VideoCallButton: React.FC<VideoCallButtonProps> = ({
  conversationId,
  agentId,
  customerInfo,
}) => {
  const calling = useCallingContext();
  if (!calling) return null;

  const { callState, startCall, endCall } = calling;

  const isIdle = callState === CallState.IDLE || callState === CallState.ENDED;
  const isPending =
    callState === CallState.INITIATING || callState === CallState.RINGING ||
    callState === CallState.CONNECTING;
  const isActive = callState === CallState.CONNECTED;

  const handleClick = async () => {
    if (isPending) return;
    if (isActive) {
      await endCall();
    } else {
      await startCall(undefined, {
        enableVideo: true,
        call_type: 'video',
        conversation_id: conversationId,
        customer_id: customerInfo.customer_id,
        agent_id: agentId,
        user_type: 'agent',
      });
    }
  };

  const tooltipTitle = isActive
    ? 'End video call'
    : isPending
    ? 'Connecting…'
    : `Video call ${customerInfo.name}`;

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={isPending}
          color={isActive ? 'error' : 'primary'}
          aria-label={tooltipTitle}
        >
          {isPending ? (
            <CircularProgress size={16} color="inherit" />
          ) : isActive ? (
            <CallEnd fontSize="small" />
          ) : (
            <Videocam fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};
